/**
 * Battle round loop — orchestrates the 5-phase engagement (§2.7) at
 * the battlescape level (v0M3.3).
 *
 * Deployment (§2.7.2) is handled at battle creation, not here — this
 * module executes rounds 1..N over an already-deployed battlescape.
 *
 * Round shape:
 *   Phase 2 Movement — cohorts execute movement orders (v0M3.3 first
 *     pass: no move logic yet, only bookkeeping to support charge
 *     trigger; move orders default to "hold").
 *   Phase 3 Combat — missile fires first (§2.7.8.10 item 14 provisional
 *     rule), then melee strikes in initiative order.
 *   Phase 4 Resolution — rout triggers, rout-morale propagation with
 *     cascade, idle stamina recovery.
 *   Phase 5 Reinforcement — deferred (§2.7.6); no reserves in v0M3.3
 *     first pass.
 *
 * Determinism: cohort iteration order is by `sortedIds` of the cohort
 * id inside each side (`MECHANICS.md` §1.1 rule 3). Strike attribution
 * uses this order.
 */

import type { RngState } from './rng';
import type { Battle, CohortOnField, Facing, Square } from './battle-state';
import { BATTLESCAPE_HEIGHT, BATTLESCAPE_WIDTH } from './battle-state';
import type { CohortId } from './state';
import {
  applyIdleRecoveryIfEligible,
  applyKillMorale,
  applyPostStrikeStamina,
  propagateRoutMorale,
  resolveMeleeStrike,
  resolveMissileFire,
  shouldRout,
  updateDefenderFacing,
} from './combat-math';
import { beginRound, hasTag, isCombatAdjacent, isMelee, isRanged } from './cohort';
import { phaseBattleAI } from './battle-ai';
import { damageWall, isCavalrySlow, isPassable, isWallCell, terrainTypeAt, WALL_DAMAGE_PER_SHOT_MULTIPLIER } from './battle-terrain';
import { BATTLESCAPE_CELL_COUNT } from './battle-state';
import { meleeTerrainToHitDelta, rangedTerrainToHitDelta } from '../data/combat-terrain';

// v0M4.1.1 — module-scope tick hint, set by advanceBattleRound so log
// events emitted in nested phase functions can stamp the world tick.
// Reset to 0 defensively at each round entry.
let battleTickHint = 0;

// ---------------------------------------------------------------------------
// Cohort iteration helpers
// ---------------------------------------------------------------------------

function sortedCohorts(cohorts: ReadonlyMap<CohortId, CohortOnField>): CohortOnField[] {
  return [...cohorts.keys()]
    .sort((a, b) => a - b)
    .map((id) => cohorts.get(id)!);
}

function allCohortsOfBattle(battle: Battle): CohortOnField[] {
  return [...sortedCohorts(battle.sides[0].cohorts), ...sortedCohorts(battle.sides[1].cohorts)];
}

function livingCohortsOfBattle(battle: Battle): CohortOnField[] {
  // v0M4.0b.2: exited cohorts are alive-but-off-field. Excluded from all
  // combat / movement / AI phases; still present for battle-conclusion
  // writeback in `side.cohorts`.
  return allCohortsOfBattle(battle).filter((c) => !c.broken && !c.exited);
}

// ---------------------------------------------------------------------------
// Phase 2 Movement
// ---------------------------------------------------------------------------

/**
 * Movement pass (v0M3.3a first pass). For each cohort with an active
 * `moveOrder`, take one 1-square cardinal step toward the target square.
 *
 * Step selection:
 *  - Choose the axis with the larger absolute delta between current and
 *    target position. On ties, prefer X-axis (determinism).
 *  - Move 1 square in the sign direction on that axis.
 *
 * Occupancy check: destination square must be unoccupied by any other
 * cohort (broken or otherwise). If blocked, the cohort holds this round
 * — it will retry next round (moveOrder stays live).
 *
 * On arrival at the targetSquare, the moveOrder is cleared (`null`).
 *
 * Bookkeeping updated: `movedThisRound = true`, `moveDirection = <axis>`
 * (so §2.7.8.3.a same-direction charge check works), `startSquare` set
 * to the pre-move position of the round (already set by `beginRound`).
 *
 * Cohorts without a moveOrder hold (movedThisRound stays false, per
 * `beginRound`).
 *
 * Broken cohorts do NOT execute movement orders — they carry their
 * flee behaviour per §2.7.5 (rout toward rear edge), which is separate
 * from moveOrder and lands as a follow-up.
 */
function phaseMovement(battle: Battle): void {
  // First pass: reset per-round bookkeeping (via beginRound). This sets
  // startSquare = current position, movedThisRound = false, moveDirection
  // = null, and clears attack/struck flags.
  for (const cohort of allCohortsOfBattle(battle)) {
    beginRound(cohort);
  }

  // Second pass: execute move orders. Iterate deterministic order (side 0,
  // then side 1, each by sortedIds) so occupancy checks resolve
  // predictably when two cohorts want the same square.
  for (const cohort of allCohortsOfBattle(battle)) {
    if (cohort.broken) continue;
    if (cohort.exited) continue; // v0M4.0b.2: exited cohorts no longer move.

    // v0M4.0b.2: hold-and-delay reactive fall-back per §2.8.1.3. When an
    // enemy is orthogonally adjacent, step 1 square rearward (opposite of
    // facing) to break contact. Simpler than the cross-round variant
    // (Michael PLAYTEST OPEN — auto-conditional-on-strike vs unconditional):
    // b.2 uses "adjacent enemy" as the trigger, which is close to
    // "was-struck-last-round" without needing round-history tracking.
    if (cohort.standingOrder === 'hold-and-delay') {
      if (hasAdjacentEnemy(cohort, battle)) {
        const rear = rearwardStep(cohort);
        const nextSquare: Square = { x: cohort.position.x + rear.dx, y: cohort.position.y + rear.dy };
        if (
          nextSquare.x >= 0 && nextSquare.x < BATTLESCAPE_WIDTH &&
          nextSquare.y >= 0 && nextSquare.y < BATTLESCAPE_HEIGHT &&
          isPassable(terrainTypeAt(battle.terrain, nextSquare.x, nextSquare.y)) &&
          !isSquareOccupied(nextSquare, battle, cohort)
        ) {
          cohort.position = nextSquare;
          cohort.movedThisRound = true;
          cohort.moveDirection = rear.direction;
        }
      }
      continue; // hold-and-delay never advances via moveOrder.
    }

    // v0M4.0d: Orderly Retreat — step 1 square rearward EVERY round
    // (unconditional, distinct from hold-and-delay's on-struck trigger).
    // Facing preserved so phaseCombat can strike orthogonal enemies who
    // follow into contact per §2.8.2. Edge arrival flagged via
    // phaseCheckExits below (same treatment as withdraw-with-haste).
    if (cohort.standingOrder === 'orderly-retreat') {
      const rear = rearwardStep(cohort);
      const nextSquare: Square = { x: cohort.position.x + rear.dx, y: cohort.position.y + rear.dy };
      if (
        nextSquare.x >= 0 && nextSquare.x < BATTLESCAPE_WIDTH &&
        nextSquare.y >= 0 && nextSquare.y < BATTLESCAPE_HEIGHT &&
        isPassable(terrainTypeAt(battle.terrain, nextSquare.x, nextSquare.y)) &&
        !isSquareOccupied(nextSquare, battle, cohort)
      ) {
        cohort.position = nextSquare;
        cohort.movedThisRound = true;
        cohort.moveDirection = rear.direction;
      }
      continue; // orderly-retreat never advances via moveOrder.
    }

    const order = cohort.moveOrder;
    if (order === null) continue;

    // Already at target?
    if (cohort.position.x === order.targetSquare.x && cohort.position.y === order.targetSquare.y) {
      cohort.moveOrder = null;
      continue;
    }

    // v0M4.0j.2 — moveSpeedCombat step-budget loop.
    // Base budget = cohort.stats.moveSpeedCombat (1 for infantry, 3 for
    // cavalry). Clamp to 1 if the cohort has the Cavalry tag AND is
    // standing on Hills/Marsh/Forest per BATTLES.md §2.8.6.3.
    let budget = cohort.stats.moveSpeedCombat;
    if (hasTag(cohort, 'Cavalry')) {
      const here = terrainTypeAt(battle.terrain, cohort.position.x, cohort.position.y);
      if (isCavalrySlow(here)) budget = 1;
    }

    for (let steps = 0; steps < budget; steps++) {
      const target = cohort.moveOrder?.targetSquare;
      if (!target) break; // cleared mid-burst (auto-halt / arrival)
      if (cohort.position.x === target.x && cohort.position.y === target.y) {
        cohort.moveOrder = null;
        break;
      }

      const step = stepWithBump(cohort, target, battle);
      if (step === null) break; // fully blocked this round — hold and try next round

      const nextSquare: Square = {
        x: cohort.position.x + step.dx,
        y: cohort.position.y + step.dy,
      };

      cohort.position = nextSquare;
      cohort.movedThisRound = true;
      cohort.moveDirection = step.direction;
      // startSquare stays at the ROUND's pre-move position (set by beginRound)
      // even across multi-step bursts — combat-math treats the whole round's
      // motion as one move for charge / flank math purposes.

      // Check arrival AFTER the step.
      if (nextSquare.x === target.x && nextSquare.y === target.y) {
        cohort.moveOrder = null;
        break;
      }

      // v0M4.0e: auto-halt on enemy contact. Fires after every step so a
      // 3-step cavalry burst doesn't overshoot past the enemy line.
      if (hasAdjacentEnemy(cohort, battle)) {
        cohort.moveOrder = null;
        break;
      }
    }
  }
}

/**
 * v0M4.0j.2 — Step-with-bump per BATTLES.md §2.8.6.3.
 *
 * Try the cardinal step toward `target` first. If that square is
 * impassable OR occupied, try the perpendicular step that gets closer
 * to the target (left-first in the axis-tangent sense — deterministic).
 * If that's also blocked, try the opposite perpendicular. Returns null
 * when all three candidates are blocked.
 *
 * Uses `cohort.position` as the origin and `battle.terrain` for
 * passability lookups. Preserves the pre-v0M4.0j "no diagonal steps"
 * discipline — combat-math + facing math depend on cardinal-only
 * movement.
 */
function stepWithBump(
  cohort: CohortOnField,
  target: Square,
  battle: Battle,
): MoveStep | null {
  const primary = chooseMoveStep(cohort.position, target);
  if (primary === null) return null;

  const candidates: MoveStep[] = [primary];
  // Perpendicular bumps — swap dx / dy roles, pick the sign that reduces
  // the remaining perpendicular delta to the target.
  if (primary.dx !== 0) {
    // Primary is horizontal — bump vertical toward target.
    const dy = target.y - cohort.position.y;
    if (dy !== 0) {
      const perp: MoveStep = dy > 0
        ? { dx: 0, dy: 1, direction: 'S' }
        : { dx: 0, dy: -1, direction: 'N' };
      candidates.push(perp);
      // Opposite perpendicular for the case where the toward-target bump
      // is also blocked (deterministic fallback).
      candidates.push(
        dy > 0
          ? { dx: 0, dy: -1, direction: 'N' }
          : { dx: 0, dy: 1, direction: 'S' },
      );
    } else {
      // Zero vertical delta — flanking with either N or S (deterministic:
      // N first per the fixed OFFSETS convention used elsewhere).
      candidates.push({ dx: 0, dy: -1, direction: 'N' });
      candidates.push({ dx: 0, dy: 1, direction: 'S' });
    }
  } else {
    // Primary is vertical — bump horizontal toward target.
    const dx = target.x - cohort.position.x;
    if (dx !== 0) {
      const perp: MoveStep = dx > 0
        ? { dx: 1, dy: 0, direction: 'E' }
        : { dx: -1, dy: 0, direction: 'W' };
      candidates.push(perp);
      candidates.push(
        dx > 0
          ? { dx: -1, dy: 0, direction: 'W' }
          : { dx: 1, dy: 0, direction: 'E' },
      );
    } else {
      candidates.push({ dx: -1, dy: 0, direction: 'W' });
      candidates.push({ dx: 1, dy: 0, direction: 'E' });
    }
  }

  // v0M4.0k.7 (2026-07-27) — Wall/Gate movement rule per MECHANICS §3
  // siege lock (Michael): walls are impassable to ENEMY cohorts only;
  // friendly (defender-side) cohorts can enter walls freely. Gate cells
  // are passable to all sides. Wall-breach (Wall HP → 0) leaves the
  // cell as Plains + drops it from `battle.wallHp`, so `isWallCell`
  // then returns false and all sides can walk through the rubble.
  //
  // Side convention (from `createSiegeBattleFromArmies`): side 0 =
  // defender (garrison + optional field army inside walls), side 1 =
  // attacker (sieger). Wall-block therefore fires only for side 1.
  const attackerSide = battle.sides[0].cohorts.has(cohort.id) ? 0 : 1;
  for (const step of candidates) {
    const nx = cohort.position.x + step.dx;
    const ny = cohort.position.y + step.dy;
    if (nx < 0 || nx >= BATTLESCAPE_WIDTH || ny < 0 || ny >= BATTLESCAPE_HEIGHT) continue;
    if (!isPassable(terrainTypeAt(battle.terrain, nx, ny))) continue;
    // Wall-block: only ENEMY-side cohorts (attackers) are stopped.
    if (attackerSide === 1 && isWallCell(battle, nx, ny)) continue;
    if (isSquareOccupied({ x: nx, y: ny }, battle, cohort)) continue;
    return step;
  }
  return null;
}

/**
 * v0M4.0b.2: rearward step direction for Hold and Delay + Orderly Retreat.
 * Opposite of the cohort's current facing. Facing N → step S (dy=+1); etc.
 */
function rearwardStep(cohort: CohortOnField): MoveStep {
  switch (cohort.facing.direction) {
    case 'N': return { dx: 0, dy: 1, direction: 'S' };
    case 'S': return { dx: 0, dy: -1, direction: 'N' };
    case 'E': return { dx: -1, dy: 0, direction: 'W' };
    case 'W': return { dx: 1, dy: 0, direction: 'E' };
  }
}

/**
 * v0M4.0b.2: true iff any enemy cohort is orthogonally adjacent to
 * `cohort`. Reuses the sides split to keep the query cheap.
 */
function hasAdjacentEnemy(cohort: CohortOnField, battle: Battle): boolean {
  // MFix4.3 — reuse the exported Chebyshev-1 `isCombatAdjacent` predicate.
  // Auto-halt on any enemy that would be combat-adjacent (including
  // diagonals) after this step, otherwise a cavalry burst can overshoot
  // past a diagonally-adjacent enemy that IS now combat-eligible.
  const cohortSide = findCohortSide(cohort, battle);
  const enemySide = cohortSide === 0 ? battle.sides[1].cohorts : battle.sides[0].cohorts;
  for (const enemy of enemySide.values()) {
    if (enemy.broken) continue;
    if (isCombatAdjacent(cohort.position, enemy.position)) return true;
  }
  return false;
}

/**
 * v0M4.0b.2: cohorts under `withdraw-with-haste` that reach a battlescape
 * edge cell exit the battle. Marked with `exited = true` (v0M4.0b.2 flag
 * on CohortOnField) rather than removed from `side.cohorts` — this keeps
 * v0M3.11 battle-conclusion writeback intact (it iterates side.cohorts
 * and copies final stamina/morale/figures back to the campaign Cohort).
 * Phase code + checkBattleEnd skip exited cohorts explicitly.
 *
 * Per §2.8.4: an exited cohort survives with its final battle stamina +
 * morale + figure count. The exit is behavioural, not lethal.
 */
function phaseCheckExits(battle: Battle): void {
  for (const side of battle.sides) {
    for (const cof of side.cohorts.values()) {
      if (cof.exited) continue;
      // v0M4.0d: orderly-retreat cohorts also exit on edge arrival per
      // §2.8.2 + §2.8.4 (Orderly Retreat "movement continues until all
      // cohorts reach the edge").
      const exitTriggeringOrder =
        cof.standingOrder === 'withdraw-with-haste' ||
        cof.standingOrder === 'orderly-retreat';
      if (!exitTriggeringOrder) continue;
      const onEdge =
        cof.position.x === 0 ||
        cof.position.x === BATTLESCAPE_WIDTH - 1 ||
        cof.position.y === 0 ||
        cof.position.y === BATTLESCAPE_HEIGHT - 1;
      if (onEdge) cof.exited = true;
    }
  }
}

interface MoveStep {
  readonly dx: -1 | 0 | 1;
  readonly dy: -1 | 0 | 1;
  readonly direction: Facing;
}

/**
 * Choose the 1-square cardinal step direction from `current` toward
 * `target`. Prefers the axis with larger absolute delta; X-axis on
 * ties (determinism). Returns null if already at target.
 */
function chooseMoveStep(current: Square, target: Square): MoveStep | null {
  const dx = target.x - current.x;
  const dy = target.y - current.y;
  if (dx === 0 && dy === 0) return null;
  const preferX = Math.abs(dx) >= Math.abs(dy) && dx !== 0;
  if (preferX || dy === 0) {
    return dx > 0
      ? { dx: 1, dy: 0, direction: 'E' }
      : { dx: -1, dy: 0, direction: 'W' };
  }
  return dy > 0
    ? { dx: 0, dy: 1, direction: 'S' }
    : { dx: 0, dy: -1, direction: 'N' };
}

/**
 * True if any cohort in the battle currently occupies `square`.
 * `excluding` is the moving cohort — its own position doesn't count as
 * "occupied by another cohort" (safe against a cohort blocking itself).
 */
function isSquareOccupied(square: Square, battle: Battle, excluding: CohortOnField): boolean {
  for (const c of allCohortsOfBattle(battle)) {
    if (c === excluding) continue;
    if (c.exited) continue; // v0M4.0b.2: exited cohorts no longer occupy squares.
    if (c.position.x === square.x && c.position.y === square.y) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Phase 3 Combat — missile first, then melee in initiative order
// ---------------------------------------------------------------------------

function findCohortSide(cohort: CohortOnField, battle: Battle): 0 | 1 {
  return battle.sides[0].cohorts.has(cohort.id) ? 0 : 1;
}

/**
 * Find every enemy cohort within cardinal adjacency of `firer`. If any
 * are adjacent, the firer cannot fire this round per §2.7.8.2.b
 * (adjacent-enemy block).
 */
function anyEnemyAdjacent(firer: CohortOnField, battle: Battle): boolean {
  const firerSide = findCohortSide(firer, battle);
  const enemySide = firerSide === 0 ? battle.sides[1].cohorts : battle.sides[0].cohorts;
  for (const enemy of enemySide.values()) {
    if (enemy.broken) continue;
    if (isCombatAdjacent(firer.position, enemy.position)) return true;
  }
  return false;
}

/**
 * Find friendly cohorts engaged in melee with the given enemy target.
 * Used for missile combined-pool friendly fire per §2.7.8.2.c sub-phase 1.
 */
function friendliesEngagedWithTarget(
  firer: CohortOnField,
  target: CohortOnField,
  battle: Battle,
): CohortOnField[] {
  const firerSide = findCohortSide(firer, battle);
  const friendlyCohorts = firerSide === 0 ? battle.sides[0].cohorts : battle.sides[1].cohorts;
  const engaged: CohortOnField[] = [];
  for (const friend of friendlyCohorts.values()) {
    if (friend === firer) continue;
    if (friend.broken) continue;
    if (isCombatAdjacent(friend.position, target.position)) engaged.push(friend);
  }
  return engaged;
}

/**
 * Choose a missile-fire target for `firer`. v0M3.3 first pass: nearest
 * enemy cohort within range (item 11 candidate default). Ties broken by
 * cohort id (lowest first) for determinism.
 *
 * v0M4.0b.3: when `skipEngagedByFriendly` is true (Fire at Will per
 * §2.8.1.2), enemy cohorts orthogonally adjacent to any same-team cohort
 * are excluded from target selection — the firer looks past melee
 * engagements for a clean target. If every in-range enemy is engaged
 * with a friendly, the firer holds fire this round (returns null).
 */
function chooseMissileTarget(
  firer: CohortOnField,
  battle: Battle,
  skipEngagedByFriendly: boolean,
): CohortOnField | null {
  const firerSide = findCohortSide(firer, battle);
  const enemySide = firerSide === 0 ? battle.sides[1] : battle.sides[0];
  const friendlySide = firerSide === 0 ? battle.sides[0] : battle.sides[1];
  let best: CohortOnField | null = null;
  let bestDist = Infinity;
  for (const enemy of enemySide.cohorts.values()) {
    if (enemy.broken) continue;
    if (enemy.exited) continue;
    const dist = Math.max(
      Math.abs(firer.position.x - enemy.position.x),
      Math.abs(firer.position.y - enemy.position.y),
    );
    if (dist > firer.stats.missileRange) continue;
    if (skipEngagedByFriendly && enemyIsEngagedWithFriendly(enemy, friendlySide.cohorts, firer)) {
      continue;
    }
    if (dist < bestDist || (dist === bestDist && (best === null || enemy.id < best.id))) {
      best = enemy;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * v0M4.0b.3: true iff any friendly cohort (not the firer itself, not
 * broken) is orthogonally adjacent to `enemy`. Used by Fire at Will to
 * skip enemies in the friendly-fire risk zone.
 */
function enemyIsEngagedWithFriendly(
  enemy: CohortOnField,
  friendlyCohorts: Map<CohortId, CohortOnField>,
  firer: CohortOnField,
): boolean {
  for (const friend of friendlyCohorts.values()) {
    if (friend === firer) continue;
    if (friend.broken) continue;
    if (friend.exited) continue;
    if (isCombatAdjacent(friend.position, enemy.position)) return true;
  }
  return false;
}

/**
 * Choose a melee target for `attacker`. v0M3.3 first pass: any cardinally-
 * adjacent enemy cohort with lowest id (determinism). If more than one
 * adjacent enemy, the lowest id is preferred.
 */
function chooseMeleeTarget(attacker: CohortOnField, battle: Battle): CohortOnField | null {
  const attackerSide = findCohortSide(attacker, battle);
  const enemySide = attackerSide === 0 ? battle.sides[1] : battle.sides[0];
  let best: CohortOnField | null = null;
  for (const enemy of enemySide.cohorts.values()) {
    if (enemy.broken) continue;
    if (!isCombatAdjacent(attacker.position, enemy.position)) continue;
    if (best === null || enemy.id < best.id) best = enemy;
  }
  return best;
}

/**
 * v0M4.2.4 — Fire the `firer` at its nearest intact wall cell within
 * missile range, applying wall damage per `WALL_DAMAGE_PER_SHOT_MULTIPLIER`.
 * Emits a `wall-strike` log event with breach status. Returns `true` if
 * a wall was targeted this call (caller should skip the cohort-target
 * path), `false` if no wall was in range.
 *
 * Deterministic (no RNG) — wall damage math is a flat multiplier so
 * siege battles re-play identically across MP hosts.
 */
function fireAtNearestWall(firer: CohortOnField, battle: Battle): boolean {
  const range = firer.stats.missileRange;
  let bestIdx = -1;
  let bestDist = Infinity;
  for (const cellIndex of battle.wallHp.keys()) {
    const wx = cellIndex % BATTLESCAPE_WIDTH;
    const wy = Math.floor(cellIndex / BATTLESCAPE_WIDTH);
    const d = Math.max(Math.abs(firer.position.x - wx), Math.abs(firer.position.y - wy));
    if (d > range) continue;
    // Deterministic tie-break: closer wins; ties by lower cell index (top-left first).
    if (d < bestDist || (d === bestDist && cellIndex < bestIdx)) {
      bestDist = d;
      bestIdx = cellIndex;
    }
  }
  if (bestIdx < 0 || bestIdx >= BATTLESCAPE_CELL_COUNT) return false;

  const damage = Math.max(1, Math.round(firer.stats.missileAttack * WALL_DAMAGE_PER_SHOT_MULTIPLIER));
  const { breached, remainingHp } = damageWall(battle, bestIdx, damage);
  battle.log.push({
    kind: 'wall-strike',
    tick: battleTickHint,
    round: battle.round,
    firerId: firer.id,
    wallCellIndex: bestIdx,
    damage,
    remainingHp,
    breached,
  });
  // Missile Supply drain: firing a shot costs 1 supply, same as anti-cohort
  // fire per §2.7.8.2. Guard against underflow.
  if (firer.missileSupply > 0) firer.missileSupply -= 1;
  // Stall watchdog: a wall strike counts as damage for stall detection —
  // otherwise a Catapult breaching walls but not killing cohorts could
  // trip the 30-round no-damage watchdog.
  battle.lastDamageRound = battle.round;
  return true;
}

/**
 * MFix3.3 — Pre-check: would `phaseCombat` do anything this round?
 *
 * Returns `true` iff at least one cohort is set up to actually strike
 * (or fire) this round. If false, callers can skip the combat sub-phase
 * entirely and transition straight to Resolution — saves the player
 * time watching nothing happen when both sides are holding.
 *
 * Predicate (matches `phaseCombat` filter logic below):
 *   - Any melee-tag cohort with an `advance-and-attack` / `flank-*`
 *     order AND a cardinally-adjacent living enemy, OR
 *   - Any ranged-tag cohort with `fire-at-will` / `aggressive-fire`
 *     AND an in-range non-adjacent target (fires miss engagement
 *     restrictions per `phaseCombat`), OR
 *   - Any Siege-tagged cohort in a siege battle with a wall in range
 *     (v0M4.2.4 wall-strike path).
 */
function combatWouldFire(battle: Battle): boolean {
  const inSiege = battle.siegeSettlement !== null;
  for (const firer of livingCohortsOfBattle(battle)) {
    // Ranged path — fire-at-will / aggressive-fire cohorts.
    if (
      isRanged(firer) &&
      (firer.standingOrder === 'fire-at-will' || firer.standingOrder === 'aggressive-fire')
    ) {
      if (anyEnemyAdjacent(firer, battle)) continue; // Would be blocked by engagement rule.
      // Wall target check for Siege+Ranged in a siege battle.
      if (inSiege && hasTag(firer, 'Siege')) {
        for (const cellIndex of battle.wallHp.keys()) {
          const wx = cellIndex % BATTLESCAPE_WIDTH;
          const wy = Math.floor(cellIndex / BATTLESCAPE_WIDTH);
          const d = Math.max(Math.abs(firer.position.x - wx), Math.abs(firer.position.y - wy));
          if (d <= firer.stats.missileRange) return true;
        }
      }
      // Cohort target check.
      const skipEngaged = firer.standingOrder === 'fire-at-will';
      if (chooseMissileTarget(firer, battle, skipEngaged) !== null) return true;
    }
    // Melee path — advance-and-attack / flank-melee / flank-ranged cohorts
    // with an adjacent enemy strike this round.
    const meleeOrder =
      firer.standingOrder === 'advance-and-attack' ||
      firer.standingOrder === 'flank-melee' ||
      firer.standingOrder === 'flank-ranged';
    if (isMelee(firer) && meleeOrder && chooseMeleeTarget(firer, battle) !== null) return true;
  }
  return false;
}

function phaseCombat(battle: Battle, rng: RngState): void {
  // Missile fire first (§2.7.8.10 item 14 provisional: missiles fire
  // before any melee strike this round).
  // v0M4.0b.3: ranged filter — fires under fire-at-will (default) or
  // aggressive-fire (friendly-fire risk). hold-fire skips entirely
  // (Missile Supply preserved). Melee-only orders on a Ranged cohort
  // (advance-and-attack / hold-and-defend / etc.) do NOT fire — such a
  // cohort will melee-strike per the melee filter below if it has the
  // Melee tag; a Ranged-only cohort with advance-and-attack does nothing
  // (spec-consistent: fire-at-will is the ranged default per §2.8.1.2).
  const rangedCohorts = livingCohortsOfBattle(battle)
    .filter((c) => isRanged(c) && (
      c.standingOrder === 'fire-at-will' ||
      c.standingOrder === 'aggressive-fire'
    ));
  // Sort by initiative descending, then cohort id for determinism.
  rangedCohorts.sort((a, b) => (b.stats.initiative - a.stats.initiative) || (a.id - b.id));

  for (const firer of rangedCohorts) {
    if (firer.broken) continue;
    if (anyEnemyAdjacent(firer, battle)) continue;

    // v0M4.2.4: siege-battle wall targeting. Cohorts with both `Siege` and
    // `Ranged` tags (v1 = Catapult with the v0M4.2.4 promotion) prefer
    // walls over enemy cohorts per BATTLES.md §3.7. Only fires when there
    // IS a wall in range; falls through to cohort-targeting otherwise.
    if (battle.siegeSettlement !== null && hasTag(firer, 'Siege')) {
      const wallHit = fireAtNearestWall(firer, battle);
      if (wallHit) continue; // Skip cohort-target path this round.
    }

    // v0M4.0b.3: Fire at Will skips enemies engaged with friendlies; Aggressive
    // Fire targets any enemy in range (friendly-fire risk downstream per
    // §2.7.8.2 combined-pool math in combat-math.ts).
    const skipEngaged = firer.standingOrder === 'fire-at-will';
    const target = chooseMissileTarget(firer, battle, skipEngaged);
    if (target === null) continue;
    const engagedFriendlies = friendliesEngagedWithTarget(firer, target, battle);
    // v0M4.0k.4 — Ranged terrain to-hit ADDITIVE DELTA per BATTLES.md §2.8.6.5
    // (was multiplicative in v0M4.0j.3). Xlsx-driven via Combat.xlsx →
    // src/data/combat-terrain.ts.
    const rangedDelta = rangedTerrainToHitDelta(
      terrainTypeAt(battle.terrain, firer.position.x, firer.position.y),
      terrainTypeAt(battle.terrain, target.position.x, target.position.y),
    );
    const result = resolveMissileFire(firer, target, engagedFriendlies, rng, rangedDelta);
    if (result.fired) {
      // v0M4.1.1 — log the missile-fire event.
      battle.log.push({
        kind: 'missile-fire',
        tick: battleTickHint,
        round: battle.round,
        firerId: firer.id,
        targetId: target.id,
        hits: result.hitCount,
        killCount: result.killCount,
        woundedCount: result.woundedCount,
        friendlyFireHits: result.friendlyFireHits,
        friendlyFireKills: result.friendlyFireKills,
        terrainMultiplier: rangedDelta,
      });
      // Stamina drain: −2 firer, −1 target cohort (missile is cohort-level).
      // Note: friendlies who take friendly-fire hits do NOT pay the −1
      // defender cost — per §2.7.8.5.a, the −1 defender cost is paid
      // by the target cohort of the strike, not incidental hits.
      applyPostStrikeStamina(firer, target, false);
      // Casualty morale (Session-28 spec): attacker +1 per enemy kill;
      // defender −1 per wound + −3 per kill. For missile fire, we exclude
      // friendly-fire casualties from the defender's morale hit — a
      // friendly-fire wound/kill is not an incoming enemy attack for the
      // target cohort. Friendly-fire wound count ≈ friendlyFireHits −
      // friendlyFireKills (each hit is either a wound or a kill).
      const enemyKills = result.killCount - result.friendlyFireKills;
      const friendlyFireWounds = Math.max(0, result.friendlyFireHits - result.friendlyFireKills);
      const enemyWounds = Math.max(0, result.woundedCount - friendlyFireWounds);
      applyKillMorale(firer, target, enemyKills, enemyWounds);
      // v0M4.0g: XP accrual. +1 per enemy figure wounded + +1 per enemy
      // figure killed by this strike (see PLAYTEST OPEN at UNITS.md §5.5
      // — first pass double-counts wound→kill). Friendly-fire kills
      // don't count as enemy XP; wound count doesn't distinguish
      // friendly vs enemy in the result shape yet so we use the enemy-
      // kill-only convention for kills but include the raw wound count
      // for wounds (small over-attribution when friendly-fire wounds).
      firer.xpEarnedThisBattle += result.woundedCount + enemyKills;
      // Friendly-fire kills: apply −1 morale to friendlies whose figures died
      // and no morale change for the firer (friendly kills don't give +1).
      if (result.friendlyFireKills > 0) {
        for (const friend of engagedFriendlies) {
          // The result doesn't tell us WHICH friend took the kills.
          // v0M3.3 simplification: distribute the friendly-fire kill
          // count proportionally to figure count. Refinement is
          // §2.7.8.10 item 17 territory.
          // For first pass: no morale change to friendlies from friendly
          // fire (the kill count they see next round via their own
          // strikes will drive their morale). This is a known
          // simplification; test 5 verifies friendly fire lands damage
          // but doesn't currently verify friendly morale.
          void friend;
        }
      }
    }
  }

  // Rout-check missile-hit victims BEFORE melee (rout events cascade).
  processRoutChecks(battle);

  // Melee strikes in initiative order.
  // v0M4.0b.2 + v0M4.0d: melee-striking orders per §2.8.1.3 + §2.8.2.
  // - advance-and-attack: strikes any adjacent enemy (base behaviour).
  // - flank-melee / flank-ranged: strike normally once in position.
  // - orderly-retreat: strikes pursuers per §2.8.2 (v0M4.0d addition).
  // - hold-and-defend, hold-and-delay, withdraw-with-haste: skip strike
  //   (no attacker stamina cost per §2.7.8.5 attacker-cost rule).
  const meleeCohorts = livingCohortsOfBattle(battle)
    .filter((c) =>
      c.standingOrder === 'advance-and-attack' ||
      c.standingOrder === 'flank-melee' ||
      c.standingOrder === 'flank-ranged' ||
      c.standingOrder === 'orderly-retreat',
    );
  meleeCohorts.sort((a, b) => (b.stats.initiative - a.stats.initiative) || (a.id - b.id));

  for (const attacker of meleeCohorts) {
    if (attacker.broken) continue;
    const target = chooseMeleeTarget(attacker, battle);
    if (target === null) continue;
    if (target.broken) continue;
    // Facing turn: defender turns to face first attacker this round.
    updateDefenderFacing(target, attacker);
    // v0M4.0k.4 — Melee terrain to-hit ADDITIVE DELTA per BATTLES.md §2.8.6.5
    // (was multiplicative in v0M4.0j.3). Xlsx-driven via Combat.xlsx.
    const meleeDelta = meleeTerrainToHitDelta(
      terrainTypeAt(battle.terrain, attacker.position.x, attacker.position.y),
      terrainTypeAt(battle.terrain, target.position.x, target.position.y),
    );
    const strike = resolveMeleeStrike(attacker, target, rng, meleeDelta);
    // v0M4.1.1 — log the melee strike (only if it actually landed hits;
    // zero-hit swings are noisy).
    if (strike.hitCount > 0) {
      battle.log.push({
        kind: 'melee-strike',
        tick: battleTickHint,
        round: battle.round,
        attackerId: attacker.id,
        defenderId: target.id,
        hits: strike.hitCount,
        killCount: strike.killCount,
        woundedCount: strike.woundedCount,
        chargeActive: strike.chargeActive,
        terrainMultiplier: meleeDelta,
      });
    }
    // Post-strike bookkeeping.
    applyPostStrikeStamina(attacker, target, strike.chargeActive);
    // Session-28: pass woundedCount so defender takes −1/wound + −3/kill.
    applyKillMorale(attacker, target, strike.killCount, strike.woundedCount);
    // v0M4.0g: XP accrual per §5.1. +1 per enemy figure wounded this
    // strike + +1 per killed. PLAYTEST OPEN: double-count wound→kill.
    attacker.xpEarnedThisBattle += strike.woundedCount + strike.killCount;
    // Rout-check after each strike (rout is instant per §2.7.8.5.c).
    processRoutChecks(battle);
    if (attacker.broken) break; // If attacker itself routed mid-round, stop.
  }
}

// ---------------------------------------------------------------------------
// Phase 4 Resolution — rout triggers + cascade + idle recovery
// ---------------------------------------------------------------------------

/**
 * Check every cohort for rout trigger. If any rout, propagate morale
 * effects to 8-adjacent cohorts and cascade until no new routs fire.
 */
function processRoutChecks(battle: Battle): void {
  const sideA = sortedCohorts(battle.sides[0].cohorts);
  const sideB = sortedCohorts(battle.sides[1].cohorts);
  const workQueue: CohortOnField[] = [];

  // Initial scan.
  for (const c of [...sideA, ...sideB]) {
    if (shouldRout(c)) {
      c.broken = true;
      // MFix7.1 (2026-07-23) — routed cohorts move toward the map edge
      // at full speed rather than standing still until battle-end.
      // Sets standingOrder = 'withdraw-with-haste' so phaseMovement
      // routes them rearward each round and phaseCheckExits marks them
      // `exited: true` on edge arrival. The cohort survives the battle
      // (fled the field per §2.8.4) with final stamina + morale + figure
      // counts intact.
      c.standingOrder = 'withdraw-with-haste';
      c.moveOrder = null;
      workQueue.push(c);
      // v0M4.1.1 — log the rout. Trigger inferred from which threshold hit.
      const trigger: 'stamina-zero' | 'morale-zero' =
        c.stamina <= 0 ? 'stamina-zero' : 'morale-zero';
      battle.log.push({
        kind: 'rout',
        tick: battleTickHint,
        round: battle.round,
        cohortId: c.id,
        trigger,
      });
    }
  }

  // Cascade — propagate morale from each newly-routed cohort; add newly
  // triggered routs to the queue.
  while (workQueue.length > 0) {
    const routed = workQueue.shift()!;
    const newlyRouted = propagateRoutMorale(routed, sideA, sideB);
    for (const c of newlyRouted) {
      // v0M4.1.1 — log cascade routs distinctly so the player can trace
      // "why did that cohort break? — because its neighbour broke".
      battle.log.push({
        kind: 'rout',
        tick: battleTickHint,
        round: battle.round,
        cohortId: c.id,
        trigger: 'cascade',
      });
    }
    workQueue.push(...newlyRouted);
  }
}

function phaseResolution(battle: Battle): void {
  processRoutChecks(battle);
  // Idle stamina recovery.
  for (const c of allCohortsOfBattle(battle)) {
    applyIdleRecoveryIfEligible(c);
  }
}

// ---------------------------------------------------------------------------
// Battle-end check + advance one round (public API)
// ---------------------------------------------------------------------------

/**
 * Battle end condition: one side entirely broken → the other wins.
 * Both entirely broken (rare in v0M3.3 first pass) → mutual-withdrawal.
 */
export function checkBattleEnd(battle: Battle): void {
  if (battle.result !== null) return;
  // v0M4.0b.2: a cohort counts as "on the field" only if not broken AND
  // not exited. A side of all-exited cohorts loses just like a side of
  // all-broken — the battle is done for them.
  const sideALive = [...battle.sides[0].cohorts.values()].some((c) => !c.broken && !c.exited);
  const sideBLive = [...battle.sides[1].cohorts.values()].some((c) => !c.broken && !c.exited);
  if (!sideALive && !sideBLive) battle.result = 'mutual-withdrawal';
  else if (!sideALive) battle.result = 'side1-victory';
  else if (!sideBLive) battle.result = 'side0-victory';
  // v0M4.1.1 — log the battle-end. Only emitted once (result was null coming in).
  if (battle.result !== null) {
    battle.log.push({
      kind: 'battle-end',
      tick: battleTickHint,
      round: battle.round,
      result: battle.result,
    });
  }
}

/**
 * Advance one battle round: Battle-AI → Movement → Combat → Resolution.
 * Increments round counter after execution.
 *
 * `phaseBattleAI` (v0M3.8a) sets fresh moveOrders on `autoAI: true`
 * battles so cohorts advance toward the nearest enemy without needing
 * player orders. On `autoAI: false` (test fixtures + future
 * player-elective battle-command interface per §6.1 #13), it's a no-op.
 */
/** Force-conclude a stalemate after N consecutive rounds with no damage. */
const STALL_LIMIT_ROUNDS = 30;

/**
 * v0M4.1.3 — Advance one sub-phase per BATTLES.md §2.9.4.
 * Fires the current phase's logic, transitions to the next phase, resets
 * `phaseStartTick`, and emits a `phase-transition` log event. End of
 * Resolution advances the round counter and runs the stall-watchdog +
 * end-of-battle checks.
 *
 * MP-safe: pure function of `battle.currentPhase` + `currentTick`.
 */
export function advanceBattleSubPhase(battle: Battle, currentTick: number): void {
  if (battle.result !== null) return;

  battleTickHint = currentTick;
  const fromPhase = battle.currentPhase;

  if (fromPhase === 'movement') {
    phaseBattleAI(battle);
    phaseMovement(battle);
    // v0M4.0b.2: withdraw-with-haste exits fire after movement so a cohort
    // that reached the edge leaves the field before combat.
    phaseCheckExits(battle);
    battle.currentPhase = 'combat';
  } else if (fromPhase === 'combat') {
    // MFix3.3: skip empty combat sub-phase. If no cohort would strike
    // this round (no melee-adjacent enemies, no ranged in-range targets,
    // no walls in range for Siege+Ranged), transition straight to
    // Resolution without waiting the combat-sub-phase duration. Saves
    // the player time watching nothing happen.
    if (!combatWouldFire(battle)) {
      battle.currentPhase = 'resolution';
    } else {
      const beforeCounts = snapshotAliveCounts(battle);
      phaseCombat(battle, battle.rng);
      // Stall-watchdog damage-marker refresh — figures killed this combat
      // sub-phase reset the no-damage counter.
      const afterCounts = snapshotAliveCounts(battle);
      if (afterCounts[0] < beforeCounts[0] || afterCounts[1] < beforeCounts[1]) {
        battle.lastDamageRound = battle.round + 1; // will match once round increments
      }
      battle.currentPhase = 'resolution';
    }
  } else if (fromPhase === 'resolution') {
    phaseResolution(battle);
    battle.round += 1;
    battle.currentPhase = 'movement';
    // End-of-round: stall check, then normal end check.
    const damageAnchor = battle.lastDamageRound < 0 ? 0 : battle.lastDamageRound;
    if (battle.round - damageAnchor > STALL_LIMIT_ROUNDS && battle.result === null) {
      battle.result = 'mutual-withdrawal';
      battle.log.push({
        kind: 'battle-end',
        tick: battleTickHint,
        round: battle.round,
        result: 'mutual-withdrawal',
      });
      return;
    }
    checkBattleEnd(battle);
  } else if (fromPhase === 'setup') {
    // Setup phase advances directly to Movement without firing any logic
    // — the player has finished repositioning. `enter_battle_command`
    // manages the actual freeze via `phase1EndTick` in battles.ts.
    battle.currentPhase = 'movement';
  }

  battle.phaseStartTick = currentTick;
  battle.log.push({
    kind: 'phase-transition',
    tick: currentTick,
    round: battle.round,
    fromPhase,
    toPhase: battle.currentPhase,
  });
}

/**
 * v0M4.1.3 compat shim — Fire all remaining sub-phases in the current
 * round back-to-back. Preserves the pre-v0M4.1.3 atomic-round semantic
 * for the ~50 tests that call `advanceBattleRound(battle)` and expect
 * one full round of everything to have run.
 *
 * Runs up to 3 sub-phases from the CURRENT `battle.currentPhase`, stopping
 * when `battle.round` increments (i.e. after a Resolution transition
 * cycles back to Movement) or when the battle concludes.
 */
export function advanceBattleRound(battle: Battle, currentTick: number = 0): void {
  if (battle.result !== null) return;
  const startRound = battle.round;
  for (let i = 0; i < 3; i++) {
    advanceBattleSubPhase(battle, currentTick);
    if (battle.result !== null) return;
    if (battle.round > startRound) return;
  }
}

/**
 * Count living (not-killed) figures across all cohorts on each side.
 * Used by the stall-watchdog to detect no-damage rounds.
 */
function snapshotAliveCounts(battle: Battle): [number, number] {
  const counts: [number, number] = [0, 0];
  for (let s = 0; s < battle.sides.length; s++) {
    const side = battle.sides[s]!;
    let n = 0;
    for (const cof of side.cohorts.values()) {
      for (const f of cof.figures) if (!f.killed) n++;
    }
    counts[s as 0 | 1] = n;
  }
  return counts;
}
